import { FC, useState } from 'react'
import cn from 'clsx'
import { datadogRum } from '@datadog/browser-rum'

import CartItem from '@components/cart/CartItem'
import { Button, Text } from '@components/ui'
import { useUI } from '@components/ui/context'
import SidebarLayout from '@components/common/SidebarLayout'
import { useCart } from '@lib/CartContext'
import usePrice from '@lib/hooks/usePrice'
import ShippingWidget from '../ShippingWidget'
import PaymentWidget from '../PaymentWidget'
import { useCheckoutContext } from '../context'

import s from './CheckoutSidebarView.module.css'

const SUBMIT_ORDER_OPERATION = 'checkout.submit_order'

const CheckoutSidebarView: FC = () => {
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [discountInput, setDiscountInput] = useState('')
  const [checkoutError, setCheckoutError] = useState(null)
  const { setSidebarView, closeSidebar } = useUI()
  const { cart: cartData, cartEmpty, cartInit, applyDiscount } = useCart()
  const { shippingRate, addressStatus, paymentStatus, handleCompleteCheckout } =
    useCheckoutContext()

  const { price: subTotal } = usePrice(
    cartData && {
      amount: Number(cartData.subtotalPrice),
      currencyCode: cartData.currency.code,
    }
  )
  const { price: total } = usePrice(
    cartData && {
      amount: Number(cartData.totalPrice),
      currencyCode: cartData.currency.code,
    }
  )

  async function handleSubmit(event: React.ChangeEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoadingSubmit(true)

    // Captured before cartInit() swaps in a fresh, empty cart. The same options
    // object goes to start and end so operationKey matches on both events.
    const operationOptions = {
      operationKey: cartData?.id,
      description: 'Submit the order and complete checkout',
      context: {
        cart_total: cartData?.totalPrice,
        item_count: cartData?.lineItems?.length,
        currency: cartData?.currency?.code,
        discounts: cartData?.discounts,
        shipping_rate: shippingRate?.price,
      },
    }

    datadogRum.startOperation(SUBMIT_ORDER_OPERATION, operationOptions)

    try {
      const res = await handleCompleteCheckout()
      if (res.error) {
        throw res.error
      }

      datadogRum.succeedOperation(SUBMIT_ORDER_OPERATION, operationOptions)

      // datadogRum.addAction('Successful Checkout', {
      //   id: cartData.id,
      //   cart_total: cartData.totalPrice,
      //   created_at: cartData.createdAt,
      //   discounts: cartData.discounts,
      // })

      // cartData.lineItems.forEach((item: any) => {
      //   datadogRum.addAction('Product Purchased', {
      //     product: {
      //       id: item.id,
      //       name: item.name,
      //       variant: item.variant.name,
      //       quantity: item.quantity,
      //       price: item.variant.price,
      //     },
      //   })
      // })

      setLoadingSubmit(false)
      await cartEmpty()
      await cartInit()
      setSidebarView('ORDER_CONFIRM_VIEW')
    } catch (e) {
      datadogRum.failOperation(SUBMIT_ORDER_OPERATION, 'error', operationOptions)
      console.log(e)
      setCheckoutError(e)
      setLoadingSubmit(false)
    }
  }

  async function handleDiscount(event: React.ChangeEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!discountInput) {
      console.error('No discount input!')
      return
    }

    try {
      const discountPath = process.env.NEXT_PUBLIC_DISCOUNTS_ROUTE
      const discountCode = discountInput.toUpperCase()
      const discountCodeUrl = `${discountPath}/discount-code?discount_code=${discountCode}`
      // call discounts service
      const res = await fetch(discountCodeUrl)

      const discount = await res.json()

      if (discount.status === 0) {
        console.log('Discount not found')
        return
      }

      await applyDiscount('FREESHIP')

      setDiscountInput('')
    } catch (err) {
      datadogRum.addError(err, {
        discount_code: discountInput,
      })
    }
  }

  return (
    <SidebarLayout
      className={s.root}
      id="sidebar"
      handleBack={() => setSidebarView('CART_VIEW')}
    >
      <div className="px-4 sm:px-6 flex-1">
        <Text variant="sectionHeading">Checkout</Text>
        {checkoutError && (
          <div className="text-red border border-red p-3 mb-3">
            {checkoutError}
          </div>
        )}

        <PaymentWidget
          isValid={paymentStatus.ok || false}
          onClick={() => setSidebarView('PAYMENT_VIEW')}
        />
        <ShippingWidget
          isValid={addressStatus.ok || false}
          onClick={() => setSidebarView('SHIPPING_VIEW')}
        />

        <ul className={s.lineItemsList}>
          {cartData?.lineItems.map((item: any) => (
            <CartItem
              key={item.id}
              item={item}
              currencyCode={cartData!.currency.code}
              variant="display"
            />
          ))}
        </ul>

        <form
          className="h-full mt-auto"
          onSubmit={handleDiscount}
          id="discount-form"
        >
          <div className={cn(s.fieldset, 'col-span-6')}>
            <label className={s.label}>Discount Code</label>
            <input
              name="discount-code"
              className={s.input}
              value={discountInput}
              onChange={(e) => setDiscountInput(e.target.value)}
            />
          </div>
          <div className="w-full">
            <Button
              type="submit"
              width="100%"
              variant="ghost"
              className="!py-2 !border-1"
              data-dd-action-name="ApplyDiscount"
            >
              Apply Discount
            </Button>
          </div>
        </form>
      </div>

      <form
        onSubmit={handleSubmit}
        id="checkout-form"
        className="flex-shrink-0 px-6 py-6 sm:px-6 sticky z-20 bottom-0 w-full right-0 left-0 bg-accent-0 border-t text-sm"
      >
        <ul className="pb-2">
          <li className="flex justify-between py-1">
            <span>Subtotal</span>
            <span>{subTotal}</span>
          </li>
          <li className="flex justify-between py-1">
            <span>Taxes</span>
            <span>Calculated at checkout</span>
          </li>
          <li className="flex justify-between py-1">
            <span>Shipping</span>
            <span className="font-bold tracking-wide" id="shipping-rate">
              {shippingRate?.price || 'TBD'}
            </span>
          </li>
        </ul>
        <div className="flex justify-between border-t border-accent-2 py-3 font-bold mb-2">
          <span>Total</span>
          <span>{total}</span>
        </div>
        <div>
          {/* Once data is correctly filled */}
          <Button
            type="submit"
            width="100%"
            loading={loadingSubmit}
            className="confirm-purchase-btn"
            data-dd-action-name="ConfirmPurchase"
            disabled={addressStatus.ok && paymentStatus.ok ? false : true}
          >
            Confirm Purchase
          </Button>
        </div>
      </form>
    </SidebarLayout>
  )
}

export default CheckoutSidebarView
